import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  capabilityAct,
  signedInRoleCodes,
  signersText,
  shortDate,
  type Capability,
  type ExceptionChain as Chain,
  type GateStage,
} from '../lib/api'
import { kindLabel } from '../lib/acceptance'
import type { Save } from '../lib/save'
import { statusWord } from '../lib/status'
import { ErrorBox, Loading } from './DataTable'
import { RefusalNote } from './RefusalNote'
import { SignatureStages } from './SignatureStages'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// Section 14's chain on one risk acceptance.
//
// The register used to draw a single Approve button, because a risk acceptance
// used to be a single signature. The requirement asks for three offices in
// turn, Business Owner then Risk or Compliance then the CISO, and the product
// had the mechanism for exactly that pointed at a different decision: it gated
// the vendor's admission to the register and not the acceptance of the risk.
//
// So this reads the same shape the vendor gate reads, and says the same things
// in the same words. Which capability signs next, who holds it and why the
// stage exists all arrive from the API, because they depend on the band and the
// tier and a console that worked them out itself would be a second copy of the
// matrix.

// The short line over the stages: where the chain stands, in a few words. A
// closed one is named in the status vocabulary's words, never its code.
export function chainLine(chain: Chain): string {
  if (chain.status !== 'PENDING') return 'Closed as ' + statusWord('exception', chain.status).word.toLowerCase()
  if (chain.complete) return 'Every stage is signed'
  const left = chain.outstanding.length
  return left + ' of ' + chain.stages.length + (left === 1 ? ' signature outstanding' : ' signatures outstanding')
}

// What is being signed, in a sentence: "this risk acceptance", "this contract
// waiver", "this acceptance" for an exception as before.
function whatWords(chain: Chain): string {
  const k = (chain.exceptionKind ?? '').toUpperCase()
  if (k === 'RISK_ACCEPTANCE' || k === 'CONTRACT_WAIVER') return 'this ' + kindLabel({ kind: k }).toLowerCase()
  return 'this acceptance'
}

function sameRole(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

// A stage name inside a sentence, as the API writes it (domain.InSentence):
// "the business owner sign-off", but "the TPRM approval".
function inSentence(name: string): string {
  if (name.length < 2 || (name[1] ?? '') !== (name[1] ?? '').toLowerCase()) return name
  return name.charAt(0).toLowerCase() + name.slice(1)
}

// Why this reader may not sign a stage, in the order the API refuses: the
// capability; the stage's office, where the stage names one (the Security and
// Risk head's stage of a risk acceptance is not the TPRM team's, though they
// hold the same capability); the rule that the person asking is none of the
// people who agree; then one person to one stage of a round. Every name is a
// stage's or a role's name, never a code. The API decides the business owner's
// stage from the vendor record, and says so if it refuses.
export function stageRefusal(
  stage: GateStage,
  mine: boolean,
  alreadySignedAt: GateStage | undefined,
  what = 'this acceptance',
  roles: string[] = signedInRoleCodes(),
): string {
  const called = stage.name || 'This stage'
  if (!can(stage.capability as Capability)) {
    const holders = signersText(stage.heldBy)
    return (
      (stage.name ? called + ' needs' : 'This stage needs') +
      ' the ability to ' +
      capabilityAct(stage.capability) +
      '. ' +
      (holders ? 'It is given by ' + holders + '.' : 'No role holds it as the matrix stands.')
    )
  }
  const office = stage.roles ?? []
  if (office.length > 0 && roles.length > 0 && !roles.some((r) => office.some((o) => sameRole(o, r)))) {
    return (
      called +
      ' is given by ' +
      (signersText(office) || 'another office') +
      ', so that it is a separate pair of eyes. Your sign-in carries none of those roles.'
    )
  }
  if (mine) {
    // The rule that matters most on this screen. An exception is a request to
    // run with a known control gap, and the person asking may not be any of the
    // people who agree to it.
    return (
      'You raised ' +
      what +
      ', so no stage of it is yours to sign. Whoever asks to carry a risk is not one of the people who agree ' +
      'to carry it.'
    )
  }
  if (alreadySignedAt) {
    return (
      'You already signed ' +
      what +
      ', at ' +
      (alreadySignedAt.name ? 'the ' + inSentence(alreadySignedAt.name) : 'another stage') +
      (alreadySignedAt.signedAt ? ', on ' + shortDate(alreadySignedAt.signedAt) : '') +
      '. Each stage needs a different person, so this one is for somebody else.'
    )
  }
  return ''
}

export function ExceptionChainPanel({
  exceptionId,
  mine,
  save,
  onGranted,
}: {
  exceptionId: string
  mine: boolean
  // The signature is one act of the save of the dialog this is drawn in
  // (lib/save.ts). This panel is drawn afresh each time the dialog opens, and
  // a busy of its own would forget a signature still out and offer the stage
  // to be signed a second time.
  save: Save
  onGranted: (note: string) => void
}) {
  const [chain, setChain] = useState<Chain | null>(null)
  // Who is reading, because the chain is satisfied by different people and not
  // by different roles.
  const [subject, setSubject] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [signError, setSignError] = useState<unknown>(null)
  const { busy, send } = save

  const load = useCallback(() => {
    setError(null)
    api.exceptionChain(exceptionId).then(setChain).catch(setError)
  }, [exceptionId])

  // Read when nothing is out: on opening, and again when a signature comes
  // back. Opened with a signature still out, the chain as it stood before it
  // would offer the same stage again once the answer freed the button, so it
  // is read after the answer and not before.
  useEffect(() => {
    if (!busy) load()
  }, [busy, load])

  useEffect(() => {
    let live = true
    api
      .me()
      .then((m) => {
        if (live) setSubject(m.subject)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const sign = useCallback(
    (stageIndex: number, note: string) => {
      setSignError(null)
      send(api.approveException(exceptionId, stageIndex, note), {
        shown: (res) => setChain(res.chain),
        done: (res) => {
          if (res.granted) onGranted(res.note ?? 'Every stage is signed and this risk is accepted.')
        },
        failed: (err) => setSignError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))),
      })
    },
    [exceptionId, onGranted, send],
  )

  if (error) return <ErrorBox error={error} what="This approval chain" />
  if (!chain) return <Loading what="the approval chain" />

  const alreadySigned = chain.stages.find((st) => st.signed && subject !== '' && st.signedBy === subject)

  return (
    <div>
      {/* A matrix with no chain for this vendor asks somebody to act, so it
          stays on the line. Otherwise the line is the count, and the API's
          account of why the chain is what it is sits behind the (i). */}
      {chain.noRow ? (
        <p className="summary-line">{chain.message}</p>
      ) : (
        <p className="summary-line sw-row">
          <span>{chainLine(chain)}</span>
          <ReasonTip label="About this chain" text={chain.message} />
        </p>
      )}
      {/* A refusal is the API's sentence alone; a failure keeps its code and
          reference for support. */}
      {signError != null && <RefusalNote error={signError} heading="Not signed" what="That signature" verb="recorded" />}
      <SignatureStages
        stages={chain.stages}
        actionable={(st) => !st.signed && st.next && chain.status === 'PENDING'}
        refusal={(st) => stageRefusal(st, mine, alreadySigned, whatWords(chain))}
        noteLabel="Note, optional, shown to whoever asks why this risk was carried"
        busy={busy}
        onSign={sign}
        actionId="tprm.exceptions.a01"
      />
    </div>
  )
}
